import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '../../shared/ui/button';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { InfoDot } from '../../shared/ui/tooltip';
import { updateSource, type TSource } from '../../entities/source';

const DisableWarning = ({ className, onFlows }: { className?: string; onFlows?: () => void }) => (
  <Note className={className}>
    Disabling a source is not a pause. A disabled source answers{' '}
    <span className="font-mono text-primary">410 Gone</span>, and senders like Stripe switch a
    webhook off permanently when they see it. To stop delivering without losing anything,{' '}
    {onFlows ? (
      <button type="button" className="text-primary underline" onClick={onFlows}>
        pause its flows
      </button>
    ) : (
      'pause its flows'
    )}{' '}
    instead.
  </Note>
);

export const SourceState = ({ source, onFlows }: { source: TSource; onFlows?: () => void }) => {
  const client = useQueryClient();
  const [asking, setAsking] = useState(false);
  const off = source.state === 'disabled';

  const toggle = useMutation({
    mutationFn: () => updateSource(source.id, { state: off ? 'active' : 'disabled' }),
    onSuccess: () => {
      setAsking(false);
      void client.invalidateQueries({ queryKey: ['source', source.id] });
      void client.invalidateQueries({ queryKey: ['sources'] });
    },
  });

  return (
    <>
      <span className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => (off ? toggle.mutate() : setAsking(true))}>
          {off ? 'Enable' : 'Disable'}
        </Button>
        {!off && (
          <InfoDot side="bottom">
            A disabled source answers 410 Gone. Senders may switch the webhook off for good. To stop
            delivering without losing anything, pause its flows instead.
          </InfoDot>
        )}
      </span>

      <Modal
        open={asking}
        onOpenChange={setAsking}
        title={`Disable ${source.name}?`}
        description="Every request is answered 410 Gone and nothing is stored."
        submit={{
          label: 'Disable',
          onSubmit: () => toggle.mutate(),
          waiting: toggle.isPending,
          waitingLabel: 'Disabling',
        }}
      >
        <DisableWarning
          onFlows={
            onFlows &&
            (() => {
              setAsking(false);
              onFlows();
            })
          }
        />
        <Note className="mt-3">
          What already arrived is untouched, and deliveries already handed to the queue still go.
        </Note>
      </Modal>
    </>
  );
};
